"use client";

import * as React from "react";
import { Check, Info, ShieldCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
const accordionItems = [{
  id: "item-1",
  title: "Headrest",
  content: "Ergonomically designed to cradle the patient's head, minimizing movement during scans. Features a contoured depression for optimal alignment and pressure distribution."
}, {
  id: "item-2",
  title: "Neck Rolls",
  content: "Provides essential cervical support. The kit includes multiple diameters to accommodate different patient anatomies and specific procedure requirements."
}, {
  id: "item-3",
  title: "Squares",
  content: "Versatile positioning aids for 8, 12, 20, 32, & 64 channel head coils, filling gaps near the sides and top of the patient's head to ensure patient stability and comfort during extended imaging sessions.  Includes 2 sizes to accommodate adult and pediatric patients."
}, {
  id: "item-4",
  title: "Wedges",
  content: "Angled supports and different sizing for precise positioning of adult and pediatric patients. Crucial for achieving specific patient stability with or without headphones and reducing motion artifacts."
}, {
  id: "item-5",
  title: "Strap",
  content: "Secure, medical-grade strap to ensure patient stability.  Position strap under the headrest pad and secure the velcro tab gently across the patient's forehead.  This helps remind patients to maintain precise positioning throughout the duration of the procedure."
}];
const visualGuideImages = [{
  src: "https://nomocopillow.com/wp-content/uploads/2026/06/image-2.png?v=07vsfianfhie",
  alt: "Illustration of the NoMoCo Pillow headrest positioning cushion.",
  label: "Headrest"
}, {
  src: "https://nomocopillow.com/wp-content/uploads/2026/06/image-3.png?v=ihzwygbfxjb",
  alt: "Illustration of the NoMoCo Pillow neck roll support cushion.",
  label: "Neck Roll"
}, {
  src: "https://nomocopillow.com/wp-content/uploads/2026/06/p3.jpg?v=7pz12rem7u",
  alt: "Illustration of the NoMoCo Pillow support wedge positioning cushion.",
  label: "Support Wedge"
}, {
  src: "https://nomocopillow.com/wp-content/uploads/2026/06/image-4.png?v=dssved087dg",
  alt: "Illustration of NoMoCo Pillow positioning square pads for patient support.",
  label: "Positioning Square"
}];
const sizeChartData = [{
  component: "Headrest Size (Sm)",
  dimensions: "11.5\" x 9\" x .5\"",
  weight: "0.5 lbs"
}, {
  component: "Headrest Size (Lg)",
  dimensions: "11.5\" x 9\" x .75\"",
  weight: "0.5 lbs"
}, {
  component: "Neck Roll (Sm)",
  dimensions: "1.5\" x 6.75\" x 2.5\"",
  weight: "0.2 lbs"
}, {
  component: "Neck Roll (Lg)",
  dimensions: "2\" x 6.75\" x 3\"",
  weight: "0.4 lbs"
}, {
  component: "Square (Sm)",
  dimensions: "3.75\" x 4.5\" x 4\"",
  weight: "0.3 lbs"
}, {
  component: "Square (Lg)",
  dimensions: "1.75\" x 4.5\" x 4.5\"",
  weight: "0.3 lbs"
}, {
  component: "Wedge (Sm) - Bolster",
  dimensions: "1/4 x 1 1/4 x 4 x 4 1/1/2",
  weight: "0.6 lbs"
}, {
  component: "Wedge (Lg) - Bolster",
  dimensions: "1/4 x 2 1/2 x 4 1/2 x 4 1/2",
  weight: "0.6 lbs"
}, {
  component: "Strap",
  dimensions: "31\" long",
  weight: "0.3 lbs"
}];
const clinicalBenefits = ["Reduces motion artifacts in imaging", "Improves patient comfort and compliance", "Radiolucent materials for clear scans", "Easy to clean and sanitize vinyl coating"];
export default function ProductDetail() {
  return <section id="productdetail" className="bg-background py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          
          {}
          <div className="lg:col-span-8 xl:col-span-9 flex flex-col gap-8">

            {}
            <div className="flex flex-col gap-4">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight">
                NoMoCo Pillow Support System
              </h1>
            </div>
            
            {}
            <div className="rounded-xl overflow-hidden border border-border shadow-sm bg-muted/30">
              <AspectRatio ratio={16 / 9}>
                <img src="https://nomocopillow.com/wp-content/uploads/2026/07/nomoco-pillow-all-products.webp?v=1" alt="NoMoCo Pillow blue memory foam positioning kit with support pads and accessories." />
              </AspectRatio>
            </div>

            {}
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-3 flex-wrap">
                <Badge variant="default" className="bg-primary/10 text-primary hover:bg-primary/20 border-primary/20">
                  <ShieldCheck className="w-3 h-3 mr-1" />
                  Clinical Grade
                </Badge>
                <Badge variant="outline" className="text-muted-foreground">
                  Radiolucent
                </Badge>
              </div>
                            
              <p className="text-lg text-muted-foreground leading-relaxed max-w-3xl">NoMoCo’s vinyl-coated MRI support system is made from high-grade, precision-cut memory foam. Its puncture-resistant coating has antibacterial qualities and is designed for repeated use. The vinyl spray coating has been rated under ASTM standards for very good resistance to fluids including betadine, blood, acid, urea, alcohol, and all-purpose cleaning products. Each pillow has four layers of coating, including a sweat-resistant topcoat to improve patient comfort.</p>
            </div>

            {}
            <Card className="border-primary/20 shadow-md bg-primary/5">
              <CardContent className="p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                <div className="flex flex-col gap-2">
                  <span className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Complete Kit Pricing</span>
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-default font-bold text-foreground">$1200.00</span>
                    <span className="text-muted-foreground">/ kit</span>
                  </div>
                  <span className="text-sm text-foreground font-medium flex items-center gap-1.5 mt-1">
                    <Check className="w-4 h-4 text-primary" /> Vinyl Coated Finish
                  </span>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                  <Button asChild size="lg" className="w-full sm:w-auto font-semibold shadow-sm">
                    <Link to="/order/">
                      Place Order Request
                    </Link>
                  </Button>
                  <Button asChild variant="outline" size="lg" className="w-full sm:w-auto bg-background">
                    <Link to="/contact/">
                      Request Quote
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Separator className="my-2" />

            {}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              
              {}
              <div className="flex flex-col gap-6">
                <h3 className="text-xl font-default font-semibold text-foreground flex items-center gap-2">
                  <Info className="w-5 h-5 text-primary" />
                  Clinical Benefits
                </h3>
                <ul className="flex flex-col gap-4">
                  {clinicalBenefits.map((benefit, i) => <li key={i} data-index={i} className="flex items-start gap-3">
                      <div className="mt-1 bg-primary/10 p-1 rounded-md shrink-0">
                        <Check className="w-4 h-4 text-primary" />
                      </div>
                      <span className="text-foreground leading-relaxed">{benefit}</span>
                    </li>)}
                </ul>
              </div>

              {}
              <div className="flex flex-col gap-6">
                <h3 className="text-xl font-default font-semibold text-foreground">
                  Kit Components
                </h3>
                <Accordion type="single" collapsible className="w-full border rounded-lg px-4 bg-card shadow-sm">
                  {accordionItems.map((item, i) => <AccordionItem key={item.id} value={item.id} data-index={i}>
                      <AccordionTrigger className="text-base font-medium hover:text-primary transition-colors">
                        {item.title}
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground leading-relaxed">
                        {item.content}
                      </AccordionContent>
                    </AccordionItem>)}
                </Accordion>
              </div>

            </div>
          </div>

          {}
          <div className="lg:col-span-4 xl:col-span-3">
            <div className="sticky top-24 flex flex-col gap-6">
              
              <Card className="shadow-md border-border overflow-hidden">
                <CardHeader className="bg-muted/30 border-b border-border pb-4">
                  <CardTitle className="text-lg font-default">Specifications & Reference</CardTitle>
                  <CardDescription>Detailed component sizing and visual guide.</CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  <Tabs defaultValue="visual" className="w-full">
                    <div className="px-6 pt-4 pb-2">
                      <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="visual">Visual Guide</TabsTrigger>
                        <TabsTrigger value="sizes">Size Chart</TabsTrigger>
                      </TabsList>
                    </div>
                    
                    <TabsContent value="visual" className="p-6 pt-2 m-0 outline-none">
                      <div className="grid grid-cols-2 gap-3">
                        {visualGuideImages.map((img, i) => <div key={i} data-index={i} className="flex flex-col gap-2 group">
                            <div className="rounded-md overflow-hidden border border-border bg-muted/20 aspect-square relative">
                              <img src={img.src} data-wvc-srcset={`${img.src}-wvc-srcset`} data-wvc-sizes={`${img.src}-wvc-sizes`} alt={img.alt} className="object-cover w-full h-full transition-transform duration-300 group-hover:scale-105" loading="lazy" />
                            </div>
                            <span className="text-xs font-medium text-center text-muted-foreground group-hover:text-foreground transition-colors">
                              {img.label}
                            </span>
                          </div>)}
                      </div>
                    </TabsContent>
                    
                    <TabsContent value="sizes" className="p-0 m-0 outline-none">
                      <Table>
                        <TableHeader>
                          <TableRow className="hover:bg-transparent">
                            <TableHead className="w-[120px]">Component</TableHead>
                            <TableHead>Dimensions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {sizeChartData.map((row, i) => <TableRow key={i} data-index={i}>
                              <TableCell className="font-medium text-xs">{row.component}</TableCell>
                              <TableCell className="text-xs text-muted-foreground">{row.dimensions}</TableCell>
                            </TableRow>)}
                        </TableBody>
                      </Table>
                      <div className="p-4 bg-muted/20 border-t border-border text-xs text-muted-foreground text-center">
                        * Dimensions are approximate and may vary slightly due to vinyl coating process.
                      </div>
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>

              {}
              

            </div>
          </div>

        </div>
      </div>
    </section>;
}